const CONSENT_ESSENTIAL = 'essential';
const CONSENT_ANALYTICS = 'analytics';
const CONSENT_VALUES = [CONSENT_ESSENTIAL, CONSENT_ANALYTICS];
const GOOGLE_COOKIE_PATTERN = /^(_ga|_ga_.+|_gid|_gat.*)$/;

let dialog = null;
let googleAnalyticsLoaded = false;

const readCookie = (name) => {
    const prefix = `${name}=`;
    const match = document.cookie.split('; ').find((part) => part.startsWith(prefix));

    return match ? decodeURIComponent(match.slice(prefix.length)) : '';
};

const writeCookie = (name, value, lifetimeDays) => {
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    const maxAge = lifetimeDays * 24 * 60 * 60;
    document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAge}; SameSite=Lax${secure}`;
};

const cookieName = () => dialog?.dataset.cookieName || 'calcimo_consent';

const measurementId = () => dialog?.dataset.googleMeasurementId || '';

/**
 * Return the stored consent decision, or an empty string when the visitor has not decided yet.
 * A stored analytics consent is ignored while Google Analytics is switched off.
 */
const storedConsent = () => {
    const value = readCookie(cookieName());
    if (!CONSENT_VALUES.includes(value)) {
        return '';
    }

    return value === CONSENT_ANALYTICS && measurementId() === '' ? CONSENT_ESSENTIAL : value;
};

const hasAnalyticsConsent = () => storedConsent() === CONSENT_ANALYTICS;

/**
 * Remove Google Analytics cookies on every domain variant they may have been set on.
 */
const removeGoogleCookies = () => {
    const host = window.location.hostname;
    const domains = ['', host, `.${host}`, `.${host.replace(/^www\./, '')}`];

    document.cookie.split('; ')
        .map((part) => part.split('=')[0])
        .filter((name) => GOOGLE_COOKIE_PATTERN.test(name))
        .forEach((name) => {
            domains.forEach((domain) => {
                document.cookie = `${name}=; path=/; max-age=0${domain ? `; domain=${domain}` : ''}`;
            });
        });
};

/**
 * Load the GA4 tag with Consent Mode v2. All storage types default to denied and only
 * analytics storage is granted, because the tag is loaded after an analytics consent only.
 */
const loadGoogleAnalytics = () => {
    const id = measurementId();
    if (googleAnalyticsLoaded || id === '') {
        return;
    }

    googleAnalyticsLoaded = true;
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() {
        window.dataLayer.push(arguments);
    };
    window.gtag('consent', 'default', {
        ad_storage: 'denied',
        ad_user_data: 'denied',
        ad_personalization: 'denied',
        analytics_storage: 'denied',
    });
    window.gtag('consent', 'update', {
        analytics_storage: 'granted',
    });
    window.gtag('set', 'ads_data_redaction', true);
    window.gtag('js', new Date());
    window.gtag('config', id, {
        allow_google_signals: false,
        allow_ad_personalization_signals: false,
    });

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
    document.head.appendChild(script);
};

const openDialog = () => {
    if (!dialog || dialog.open) {
        return;
    }

    dialog.showModal();
    dialog.querySelector('[data-cookie-consent-choice]')?.focus();
};

const closeDialog = () => {
    if (dialog?.open) {
        dialog.close();
    }
};

const saveConsent = (value) => {
    if (!CONSENT_VALUES.includes(value)) {
        return;
    }

    writeCookie(cookieName(), value, Number(dialog?.dataset.cookieLifetimeDays) || 365);
    closeDialog();

    if (value === CONSENT_ANALYTICS) {
        loadGoogleAnalytics();
    } else {
        removeGoogleCookies();
        // A loaded tag cannot be unloaded, so a withdrawn consent needs a fresh page.
        if (googleAnalyticsLoaded) {
            window.gtag('consent', 'update', { analytics_storage: 'denied' });
            window.location.reload();
            return;
        }
    }

    document.dispatchEvent(new CustomEvent('calcimo:consent-changed', { detail: { consent: value } }));
};

/**
 * Show the consent dialog until a decision is stored and load Google Analytics only
 * when the visitor has granted the analytics consent.
 */
export const initCookieConsent = () => {
    dialog = document.querySelector('[data-cookie-consent]');
    if (!dialog || typeof dialog.showModal !== 'function') {
        return;
    }

    dialog.querySelectorAll('[data-cookie-consent-choice]').forEach((button) => {
        button.addEventListener('click', () => saveConsent(button.dataset.cookieConsentChoice));
    });

    // Without a stored decision the dialog must stay open, so Escape cannot dismiss it.
    dialog.addEventListener('cancel', (event) => {
        if (storedConsent() === '') {
            event.preventDefault();
        }
    });

    document.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-cookie-consent-open]');
        if (trigger) {
            event.preventDefault();
            openDialog();
        }
    });

    window.calcimoConsent = {
        open: openDialog,
        hasAnalyticsConsent,
    };

    if (hasAnalyticsConsent()) {
        loadGoogleAnalytics();
    } else {
        removeGoogleCookies();
    }

    if (storedConsent() === '') {
        openDialog();
    }
};
